import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Settings, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InfiniteScrollSentinel } from "@/components/shared/InfiniteScrollSentinel";
import { useNotificationsList } from "@/hooks/app/useNotifications";
import { useNotificationActions } from "@/hooks/app/useNotificationActions";
import { useRealtimeNotifications } from "@/hooks/app/useRealtimeNotifications";
import { NotificationItem } from "./NotificationItem";
import { NotificationPreferencesModal } from "./NotificationPreferencesModal";
import { ClearReadDialog } from "./ClearReadDialog";
import { TABS, TAB_EMPTY_STATE, tabToOptions, splitByReadState, type TabKey } from "./notificationTabs";

function GroupHeader({ children }: { children: ReactNode }) {
  return (
    <div className="sticky top-0 z-10 bg-background/95 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur supports-[backdrop-filter]:bg-background/80">
      {children}
    </div>
  );
}

function NotificationSkeleton() {
  return (
    <div className="divide-y">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-start gap-3 p-3">
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted" />
          <div className="flex-1 space-y-2 py-0.5">
            <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-2.5 w-20 animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ tab }: { tab: TabKey }) {
  const { title, description } = TAB_EMPTY_STATE[tab];
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center text-muted-foreground">
      <Bell className="mb-2 h-8 w-8 opacity-50" />
      <p className="font-medium">{title}</p>
      <p className="text-xs">{description}</p>
    </div>
  );
}

export function NotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showClearRead, setShowClearRead] = useState(false);
  const [tab, setTab] = useState<TabKey>("nao_lidas");

  const scrollRootRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsList(tabToOptions(tab));

  const { markAllAsRead, isMarkingAllAsRead, clearRead } = useNotificationActions();

  // Enable realtime notifications
  useRealtimeNotifications();

  // Ancora o IntersectionObserver do "carregar mais" no container de scroll;
  // sem isso ele usaria o viewport do documento e carregaria todas as páginas de uma vez.
  useEffect(() => {
    if (!isOpen) return;
    setViewport(scrollRootRef.current);
  }, [isOpen]);

  const { novas, anteriores } = splitByReadState(notifications);
  const showHeaders = novas.length > 0 && anteriores.length > 0;

  const renderGroup = (label: string, items: typeof notifications) =>
    items.length > 0 && (
      <section>
        {showHeaders && <GroupHeader>{label}</GroupHeader>}
        <div className="divide-y">
          {items.map((n) => (
            <NotificationItem key={n.id} notification={n} variant="popup" onClose={() => setIsOpen(false)} />
          ))}
        </div>
      </section>
    );

  return (
    <>
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          // Diálogos abertos a partir do popover (limpar lidas / preferências)
          // roubam o foco e fechariam o popover no meio da ação — segura aberto.
          if (!open && (showClearRead || showPreferences)) return;
          setIsOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative h-9 w-9 min-h-9 min-w-9 text-muted-foreground hover:text-foreground">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full px-1 text-[10px] leading-none flex items-center justify-center tabular-nums"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        {/* Largura/altura fluidas: encolhe em viewport estreito e respeita a altura
            disponível calculada pelo Radix (evita footer fora da tela). */}
        <PopoverContent
          align="end"
          collisionPadding={8}
          className="flex w-[min(26rem,calc(100vw-1rem))] max-h-[min(37.5rem,var(--radix-popover-content-available-height))] flex-col overflow-hidden p-0"
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b p-3 pl-4">
            {/* Sem título redundante ("Notificações" já é o contexto do sino):
                o espaço vai para as ações; a contagem vira o próprio rótulo. */}
            <span className="min-w-0 truncate text-xs font-semibold tabular-nums">
              {unreadCount > 0
                ? `${unreadCount > 99 ? "99+" : unreadCount} não lida${unreadCount > 1 ? "s" : ""}`
                : "Notificações"}
            </span>
            {/* Ações em massa sempre visíveis no popover (sem kebab): rótulos
                somem só em viewport muito estreito. */}
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                variant="ghost"
                size="xs"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => markAllAsRead()}
                disabled={isMarkingAllAsRead || unreadCount === 0}
                aria-label="Marcar todas como lidas"
                title="Marcar todas como lidas"
              >
                <CheckCheck className="h-4 w-4" />
                <span className="hidden min-[420px]:inline">Marcar lidas</span>
              </Button>
              <Button
                variant="ghost"
                size="xs"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => setShowClearRead(true)}
                aria-label="Limpar notificações lidas"
                title="Limpar notificações lidas"
              >
                <Trash2 className="h-4 w-4" />
                <span className="hidden min-[420px]:inline">Limpar lidas</span>
              </Button>
              <Button
                variant="ghost"
                size="xs"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setShowPreferences(true)}
                aria-label="Preferências"
                title="Preferências"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="shrink-0 border-b px-2">
            <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
              {/* Sem scrollbar visível: as abas cabem na largura do popover;
                  se faltar 1-2px em viewport estreito, o overflow rola sem barra. */}
              <TabsList
                variant="underline"
                className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {TABS.map((t) => (
                  <TabsTrigger key={t.key} value={t.key} variant="underline" className="shrink-0 px-2 py-2 text-xs">
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Scroll nativo de propósito: o viewport do Radix ScrollArea envolve o
              conteúdo num display:table que expande com texto longo e estoura a
              largura do popover (linhas clipadas na borda direita). */}
          <div ref={scrollRootRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {isLoading ? (
              <NotificationSkeleton />
            ) : notifications.length === 0 ? (
              <EmptyState tab={tab} />
            ) : (
              <div>
                {renderGroup("Novas", novas)}
                {renderGroup("Anteriores", anteriores)}
                {viewport && (
                  <InfiniteScrollSentinel
                    onLoadMore={fetchNextPage}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    rootElement={viewport}
                    rootMargin="120px"
                  />
                )}
              </div>
            )}
          </div>

          <div className="shrink-0 border-t p-2">
            <Button asChild variant="ghost" size="sm" className="w-full justify-center text-xs">
              <Link to="/notifications" onClick={() => setIsOpen(false)}>
                Ver todas as notificações
              </Link>
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ClearReadDialog
        open={showClearRead}
        onOpenChange={setShowClearRead}
        onConfirm={() => clearRead()}
      />

      <NotificationPreferencesModal
        open={showPreferences}
        onClose={() => setShowPreferences(false)}
      />
    </>
  );
}
